import React from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import EditIcon from "../../../assets/icon/Edit.svg";
import { getAwardTheme } from "../goldenAwardTheme";

import "./goldenAwardQuestion.css";

export const getQuestionState = (question) => {
  if (question.winner_published && question.winners?.length > 0) return "WINNER";
  if (question.correct_answer != null) return "RESULT";
  if (!question.prediction_open) return "LOCKED";
  if (question.user_answer != null) return "ANSWERED";
  return "PENDING";
};

const getInitials = (name) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");

// Shared RESULT-state visual — same "how did I do" card in both the
// question list (GoldenAwardQuestion) and the modal's read-only slide
// (MobileGoldenAwards' GoldenAwardModalSlide), so the two never drift.
export const GoldenAwardResult = ({ question }) => {
  const isEn = localStorage.language == "en";
  const outcome = !question.user_answer
    ? "unanswered"
    : question.user_answer === question.correct_answer
      ? "correct"
      : "incorrect";

  return (
    <div className={`goldenAwardQuestion-result goldenAwardQuestion-result-${outcome}`}>
      <div className="goldenAwardQuestion-result-icon">
        {outcome === "correct" && <CheckCircleIcon fontSize="inherit" />}
        {outcome === "incorrect" && <CancelIcon fontSize="inherit" />}
        {outcome === "unanswered" && <HelpOutlineIcon fontSize="inherit" />}
      </div>
      <div className="goldenAwardQuestion-result-body">
        <p className="goldenAwardQuestion-result-headline">
          {outcome === "correct" && (isEn ? "You got it right!" : "നിങ്ങൾ ശരിയായി ഊഹിച്ചു!")}
          {outcome === "incorrect" && (isEn ? "Not quite" : "ശരിയായില്ല")}
          {outcome === "unanswered" && (isEn ? "No answer submitted" : "ഉത്തരം നൽകിയിട്ടില്ല")}
        </p>
        <p className="goldenAwardQuestion-result-detail">
          {isEn ? "Correct answer: " : "ശരിയായ ഉത്തരം: "}
          <span className="goldenAwardQuestion-result-detail-value">
            {question.correct_answer}
          </span>
        </p>
        {outcome === "incorrect" && (
          <p className="goldenAwardQuestion-result-detail goldenAwardQuestion-result-detail-muted">
            {isEn ? "Your pick: " : "നിങ്ങളുടെ ഉത്തരം: "}
            <span className="goldenAwardQuestion-result-detail-value">{question.user_answer}</span>
          </p>
        )}
      </div>
    </div>
  );
};

const GoldenAwardQuestion = ({ question, onEditRequest }) => {
  const state = getQuestionState(question);
  const isLoggedIn = !!localStorage.webKey;

  // PENDING questions are answered via the popup dialog hoisted in
  // MobileGoldenAwards.js, not rendered inline here. WINNER questions
  // render as GoldenAwardWinner instead, also hoisted there.
  if (state === "PENDING" || state === "WINNER") return null;

  // Always match keywords against the English text, even when the
  // Malayalam text is what's actually displayed.
  const theme = getAwardTheme(question.question_text);

  return (
    <div className={`goldenAwardQuestion goldenAwardQuestion-state-${state.toLowerCase()}`}>
      <div className="goldenAwardQuestion-header">
        <div className="goldenAwardQuestion-badge">
          {theme.image ? (
            theme.tinted ? (
              <span
                className="goldenAwardQuestion-badgeImg goldenAwardQuestion-badgeImg-tinted"
                style={{ WebkitMaskImage: `url(${theme.image})`, maskImage: `url(${theme.image})` }}
              />
            ) : (
              <img src={theme.image} alt="" className="goldenAwardQuestion-badgeImg" />
            )
          ) : (
            <theme.Icon fontSize="inherit" />
          )}
        </div>
        <p className="goldenAwardQuestion-text">{localStorage.language == "en" ? question.question_text : question.question_text_malayalam}</p>
      </div>

      {state === "ANSWERED" && (
        <div
          className="goldenAwardQuestion-answered"
          style={{ borderColor: theme.accent }}
          onClick={() => onEditRequest(question.question_id)}
        >
          <div className="goldenAwardQuestion-answered-avatar" style={{ background: theme.accent }}>
            {getInitials(question.user_answer)}
          </div>
          <div className="goldenAwardQuestion-answered-info">
            <span className="goldenAwardQuestion-answered-label">
              {localStorage.language == "en" ? "Your pick" : "നിങ്ങളുടെ ഉത്തരം"}
            </span>
            <span className="goldenAwardQuestion-answered-value">{question.user_answer}</span>
          </div>
          <div className="goldenAwardQuestion-edit">
            <img src={EditIcon} alt="Edit" />
          </div>
        </div>
      )}

      {state === "LOCKED" && (
        <div className="goldenAwardQuestion-locked">
          <span className="goldenAwardQuestion-locked-label">
            {!isLoggedIn
              ? localStorage.language == "en"
                ? "Time's up!"
                : "സമയം കഴിഞ്ഞു!"
              : question.user_answer
                ? (localStorage.language == "en" ? "Your pick: " : "നിങ്ങളുടെ ഉത്തരം: ") +
                  question.user_answer
                : localStorage.language == "en"
                  ? "You didn't answer this one"
                  : "നിങ്ങൾ ഇതിന് ഉത്തരം നൽകിയിട്ടില്ല"}
          </span>
        </div>
      )}

      {state === "RESULT" && <GoldenAwardResult question={question} />}
    </div>
  );
};

export default GoldenAwardQuestion;
